import { useState, useEffect } from "react";

// ---- Global beforeinstallprompt capture (module loads once, early) ----
let deferredPrompt = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredPrompt = e;
    window.dispatchEvent(new Event("ponesi-install-available"));
  });
  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    setDismissedAt(); // don't re-prompt after install
    window.dispatchEvent(new Event("ponesi-installed"));
  });
}

// ---- Platform detection ----
export function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return !!mq || window.navigator.standalone === true;
}

function ua() {
  return (typeof navigator !== "undefined" && navigator.userAgent) || "";
}

export function isIOS() {
  return /iPad|iPhone|iPod/.test(ua()) ||
    (typeof navigator !== "undefined" && navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

// Common embedded / in-app browsers where Add-to-Home-Screen is unavailable.
export function isInAppBrowser() {
  return /(FBAN|FBAV|FB_IAB|Instagram|Line\/|WhatsApp|Snapchat|Viber|MicroMessenger|; wv\)|GSA\/)/i.test(ua());
}

export function getDeferredPrompt() {
  return deferredPrompt;
}

// Resolve the appropriate install experience for the current device/context.
export function installMode() {
  if (isStandalone()) return "installed";
  if (deferredPrompt) return "android"; // Chromium mobile + desktop
  if (isIOS()) return isInAppBrowser() ? "ios-inapp" : "ios";
  return "none"; // no install opportunity (unsupported / not yet eligible)
}

export async function triggerInstall() {
  if (!deferredPrompt) return { outcome: "unavailable" };
  deferredPrompt.prompt();
  const choice = await deferredPrompt.userChoice;
  if (choice.outcome === "accepted") deferredPrompt = null;
  return choice;
}

// ---- Dismissal (session + 7-day cooldown) ----
const DISMISS_KEY = "ponesi_install_prompt_dismissed_at";
const SESSION_KEY = "ponesi_install_session_dismissed";
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

export function setDismissedAt() {
  try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch {}
  try { sessionStorage.setItem(SESSION_KEY, "1"); } catch {}
}

export function dismissedRecently() {
  try {
    if (sessionStorage.getItem(SESSION_KEY) === "1") return true;
    const ts = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
    return ts > 0 && Date.now() - ts < COOLDOWN_MS;
  } catch { return false; }
}

// ---- Order-critical suppression (ref-counted, cross-component) ----
let suppressCount = 0;

function emitSuppress() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("ponesi-install-suppress", { detail: suppressCount }));
  }
}

export function suppressInstall() { suppressCount += 1; emitSuppress(); }
export function releaseInstall() { suppressCount = Math.max(0, suppressCount - 1); emitSuppress(); }
export function currentSuppress() { return suppressCount; }

// Hook: suppress the install prompt while `active` is true (e.g. a critical modal is open).
export function useInstallSuppress(active) {
  useEffect(() => {
    if (!active) return;
    suppressInstall();
    return () => releaseInstall();
  }, [active]);
}

// Hook: reactive install mode for optional entry points (e.g. Settings button).
export function useInstallMode() {
  const [mode, setMode] = useState(installMode());
  useEffect(() => {
    const update = () => setMode(installMode());
    window.addEventListener("ponesi-install-available", update);
    window.addEventListener("ponesi-installed", update);
    document.addEventListener("visibilitychange", update);
    return () => {
      window.removeEventListener("ponesi-install-available", update);
      window.removeEventListener("ponesi-installed", update);
      document.removeEventListener("visibilitychange", update);
    };
  }, []);
  return mode;
}

// Manually open the install invitation (e.g. from Settings). Respects installed/suppress rules.
export function openInstallPrompt() {
  if (typeof window !== "undefined") window.dispatchEvent(new Event("ponesi-open-install"));
}

import { track } from "./analytics";

// ---- Lightweight analytics (first-party, no external vendor) ----
export function trackInstall(event, extra = {}) {
  try {
    // First-party mapping: the PWA prompt lifecycle lands in Ponesi analytics.
    const map = {
      PWA_INSTALL_PROMPT_SHOWN: "install_prompt_shown",
      PWA_IOS_INSTRUCTIONS_SHOWN: "install_prompt_shown",
      PWA_INSTALL_DISMISSED: "install_prompt_dismissed",
      PWA_INSTALL_CLICKED: "install_prompt_accepted",
      PWA_INSTALLED: "install_prompt_accepted",
    };
    const name = map[event];
    if (name) track(name, { ...extra, prompt_event: event, dedupe_key: event });
    // eslint-disable-next-line no-console
    console.debug("[pwa]", event, extra);
  } catch {}
}
